import "../../styles/clientPage.css";
import { useState, useMemo, useEffect } from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import SlideOver from "../Common/SlideOver";
import VisibilityIcon from "../../assets/client/visibility-off-icon.svg";
import WorkOutLineIcon from "../../assets/client/work-out-line-icon.svg";
import PersonVideoIcon from "../../assets/client/person-video-icon.svg";
import { ClientStatsType } from "../../utils/types";
import ClientInteractionSlideOverview from "./ClientInteractionSlideOverview";
import ArrowUp from "../../assets/common/arrow-up-green.svg";
import ArrowDown from "../../assets/common/arrow-down-red.svg";
import ArrowUpRed from "../../assets/common/arrow-up-red.svg";
import ArrowDownGreen from "../../assets/common/arrow-down-green.svg";
import { useLocation } from "react-router-dom";
import { normalizeToFullDay } from "../../utils/utils";

interface ClntInteractionProps {
  clientStatsData: ClientStatsType[];
  showSpinner?: boolean;
  parentPageIdentifier?: string;
}

const ClientInteractionQuality = ({ clientStatsData, showSpinner, parentPageIdentifier }: ClntInteractionProps) => {
  const location = useLocation();
  const INTERACTION_FILTER_KEY = "interactionQualityFilter";
  const savedFilter = localStorage.getItem(INTERACTION_FILTER_KEY);
  const initialFilter = savedFilter || location.state?.appliedFilters || "";
  const [selectedFilter, setSelectedFilter] = useState<string>(initialFilter);
  const [isSlideOverOpen, setIsSlideOverOpen] = useState(!!initialFilter);
  const [filterResetSignal, setFilterResetSignal] = useState(0);
  const slideOverParent = "client-interaction-overview";
  const incomingSlideOverParent = location.state?.slideOverParent;
  const filters = location.state?.appliedFilters;
  const INTERACTION_DATE_RANGE_KEY = "businessOverviewFilters";
  const savedDateRange = localStorage.getItem(INTERACTION_DATE_RANGE_KEY);
  const [customDateRange, setCustomDateRange] = useState<
    { startDate: string; endDate: string } | undefined
  >(
    savedDateRange
      ? JSON.parse(savedDateRange)
      : location.state?.customDateRange
  );
  const [hoveredData, setHoveredData] = useState<Record<string, {count: number; change: number; weekStart: string;}>>({});
  const incomingCustomDateRange = location.state?.customDateRange;  
  const openSlideOverWithFilter = (filterKey: string) => {
    setSelectedFilter(filterKey);
    localStorage.setItem(INTERACTION_FILTER_KEY, filterKey);
    setIsSlideOverOpen(true);
  };

  useEffect(() => {
    if (customDateRange) {
      localStorage.setItem(
        INTERACTION_DATE_RANGE_KEY,
        JSON.stringify(customDateRange)
      );
    }
  }, [customDateRange]);  

  useEffect(() => {
    if (incomingSlideOverParent === slideOverParent) {
      if (filters) {
        setSelectedFilter(filters);
        localStorage.setItem(INTERACTION_FILTER_KEY, filters);
      }
  
      if (incomingCustomDateRange) {
        setCustomDateRange(incomingCustomDateRange);
      } else if (savedDateRange) {
        setCustomDateRange(JSON.parse(savedDateRange));
      }
  
      setIsSlideOverOpen(true);
    }
  }, [incomingSlideOverParent]);
  

  const handleBarClickWithDate = (filterKey: string, weekStart: string) => {
    const start = new Date(weekStart);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
  
    const {startDate: startDateUTC, endDate: endDateUTC} = normalizeToFullDay(start, end);

    setCustomDateRange({
      startDate: startDateUTC.toISOString(),
      endDate: endDateUTC.toISOString(),
    });
  
    openSlideOverWithFilter(filterKey);
  };  

  // Prepare interaction items with 4-week trend data
  const interactionItems = useMemo(() => {
    if (!clientStatsData?.length) return [];
  
    const weeks = [...clientStatsData]
      .filter((w) => w?.weekStart)
      .sort((a, b) => new Date(b.weekStart).getTime() - new Date(a.weekStart).getTime())
      .slice(0, 5)
      .reverse();
  
    const items = [
      {
        icon: VisibilityIcon,
        label: "Calls where Client did not show up",
        key: "clientNoShow",
        filterKey: "clientnoshow",
      },
      {
        icon: WorkOutLineIcon,
        label: "Calls where explicit agenda was set",
        key: "explicitAgendas",
        filterKey: "explicitagenda",
      },
      {
        icon: PersonVideoIcon,
        label: "Calls where Rep did not show up",
        key: "accountNoShow",
        filterKey: "accountnoshow",
      },
    ];
  
    return items.map((item) => {
  
      // Raw counts only
      const counts = weeks.map((week: any) => week[item.key] || 0);
  
      // Bar height scaling based on visible counts
      const visibleCounts = counts.slice(1);
      const max = Math.max(...visibleCounts, 1);
      const heightBars = counts.map((val, i) => i === 0 ? 5 : (val / max) * 100);
  
      // Week-over-week change using counts
      const last = counts[counts.length - 1] ?? 0;
      const prev = counts.length > 1 ? counts[counts.length - 2] : 0;
  
      const change =
        prev === 0
          ? last === 0 ? 0 : 100
          : ((last - prev) / prev) * 100;
  
      return {
        ...item,
        bars: heightBars,
        counts,
        weekStarts: weeks.map((week: any) => week.weekStart),
        change,
      };
    });
  
  }, [clientStatsData]);  

  useEffect(() => {
    if (interactionItems.length > 0) {
      const initial: Record<string, any> = {};
  
      interactionItems.forEach((item) => {
        const lastIndex = item.counts.length - 1;
        const prevIndex = lastIndex > 0 ? lastIndex - 1 : null;
  
        const lastVal = item.counts[lastIndex] ?? 0;
        const prevVal =
          prevIndex !== null ? item.counts[prevIndex] ?? 0 : 0;
  
          const change = prevVal === 0 ? lastVal === 0 ? 0 : 100 : ((lastVal - prevVal) / prevVal) * 100;        
  
        initial[item.key] = {
          count: lastVal,
          change,
          weekStart: item.weekStarts[lastIndex],
        };
      });
  
      setHoveredData(initial);
    }
  }, [interactionItems]);  

  const handleBarHover = (item: any, barIndex: number) => {
    const currentVal = item.counts[barIndex] ?? 0;
    const prevVal =
      barIndex > 0 ? item.counts[barIndex - 1] ?? 0 : 0;
  
      const change =
      prevVal === 0
        ? currentVal === 0 ? 0 : 100
        : ((currentVal - prevVal) / prevVal) * 100;

  
    setHoveredData((prev) => ({
      ...prev,
      [item.key]: {
        count: currentVal,
        change,
        weekStart: item.weekStarts[barIndex],
      },
    }));
  };  
  
  const handleBarLeave = (item: any) => {
    const lastIndex = item.counts.length - 1;
    const prevIndex = lastIndex > 0 ? lastIndex - 1 : null;
  
    const lastVal = item.counts[lastIndex] ?? 0;
    const prevVal =
      prevIndex !== null ? item.counts[prevIndex] ?? 0 : 0;
  
      const change =
        prevVal === 0
          ? lastVal === 0 ? 0 : 100
          : ((lastVal - prevVal) / prevVal) * 100;
  
    setHoveredData((prev) => ({
      ...prev,
      [item.key]: {
        count: lastVal,
        change,
        weekStart: item.weekStarts[lastIndex],
      },
    }));
  };   

  const formatWeekEnding = (weekStart: string) => {
    const start = new Date(weekStart);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
  
    return end.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };  

  const getTooltipMetricLabel = (itemLabel: string) => {
    switch (itemLabel) {
      case "Calls where explicit agenda was set":
        return "Explicit agenda set";
      case "Calls where Client did not show up":
        return "Client no-show";
      case "Calls where Rep did not show up":
        return "Rep no-show";
      default:
        return itemLabel;
    }
  };
  
  const getChangeVisuals = (label: string, change: number) => {
    const reverse =
      label === "Calls where Client did not show up" ||
      label === "Calls where Rep did not show up";
  
    if (reverse) {
      return {
        color: change >= 0 ? "#FF4D4D" : "#1BB761",
        arrow: change >= 0 ? ArrowUpRed : ArrowDownGreen,
      };
    }
  
    return {
      color: change >= 0 ? "#1BB761" : "#FF4D4D",
      arrow: change >= 0 ? ArrowUp : ArrowDown,
    };
  };  
  return (
    <div className="section">
      <div className="section__header">
        <h2 className="section__title">Interaction Quality</h2>
        <div className="section__controls">
          <span className="section__dropdown">Trailing 4-weeks</span>
        </div>
      </div>

      <div className="interaction-quality" style={{paddingTop: "10px"}}>
        
      {showSpinner
        ? Array.from({ length: 3 }).map((_, idx) => (
          <div key={`shimmer-${idx}`} className="shimmer-interaction-card">
            <div className="shimmer-interaction-top">
              <div className="shimmer-icon" />
              <div className="shimmer-label" />
              <div className="shimmer-bar" />
            </div>
          </div>
        ))
        : 
        interactionItems.map((item, index) => {
          const currentChange = hoveredData[item.key]?.change ?? item.change;
          const hoveredWeekStart = hoveredData[item.key]?.weekStart;
          const hoveredIndex = item.weekStarts.findIndex((w: string) => w === hoveredWeekStart);
          const currentIndex = hoveredIndex !== -1 ? hoveredIndex : item.counts.length - 1;
          const currentVal = item.counts[currentIndex] ?? 0;
          const prevVal = item.counts[currentIndex - 1] ?? 0;
          const isNewIncrease = prevVal === 0 && currentVal > 0;
          const { color, arrow } = getChangeVisuals(item.label, currentChange);
          return (
          <div className="interaction-card" key={index}>
            <div className="interaction-card__info">
              <div className="interaction-card__img--box">
                <img
                  src={item.icon}
                  alt={item.label}
                  className="interaction-card__icon-img"
                />
              </div>
            <div className="interaction-card__title_container">
              <span className="interaction-card__label" 
                onClick={() => handleBarClickWithDate(item.filterKey, item.weekStarts[item.weekStarts.length - 1])}                
                style={{ cursor: "pointer" }}>{`${item.label}:`}</span>
              <div className="interaction-card__metrics">
                <span
                  className="interaction-card__count"
                  onClick={() => handleBarClickWithDate(item.filterKey, item.weekStarts[item.weekStarts.length - 1])}                  
                  style={{ cursor: "pointer", color: "#000000" }}
                >
                  {hoveredData[item.key]?.count ?? item.counts[item.counts.length - 1]}
                </span>
                  <span
                    className="interaction-card__change"
                    style={{
                      color: color,
                      fontWeight: 400,
                      paddingLeft: "4px",
                      paddingTop: "2px",
                      display: "inline-flex",
                      alignItems: "center",
                      fontSize: "14px"
                    }}
                  >
                    [
                    <span style={{ margin: "0 4px" }}>
                    {Math.round(Math.abs(currentChange))}%
                    {isNewIncrease && <span style={{ marginLeft: "2px" }}>+</span>}
                    </span>
                    <img
                      src={arrow}
                      alt={currentChange >= 0 ? "Increase" : "Decrease"}
                      style={{
                        width: 12,
                        height: 10,
                        marginLeft: "2px",
                      }}
                    />
                    ]
                  </span>
                </div>
              </div>
            </div>
            <Box className="client-call-quality__card">
              <Box className="client-call-quality__bars-container" onMouseLeave={() => handleBarLeave(item)}>
                {item.bars.map((barValue: number, index: number) => {
                  if (index === 0) return null;
                  return (
                  <Tooltip
                  key={index}
                  arrow
                  placement="top"
                  title={
                    <Box sx={{ textAlign: "left" }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                        Week Ending: {formatWeekEnding(item.weekStarts[index])}
                      </Typography>
                      <Typography sx={{ fontSize: 12 }}>
                        {getTooltipMetricLabel(item.label)}: {item.counts?.[index] ?? 0}
                      </Typography>
                    </Box>
                  }
                >
                  <Box className="client__stats__bar-wrapper">
                
                    <Box
                      className="client__stats__bar-container"
                      sx={{ height: "50px" }}
                    >
                      <Box
                        sx={{
                          height: barValue === 0
                            ? "5px"
                            : `${barValue}%`,
                          width: "21px",
                          borderRadius: "4px",
                          background:
                            hoveredData[item.key]?.weekStart === item.weekStarts[index] &&
                            index === item.bars.length - 1 && barValue > 0
                              ? "linear-gradient(180deg, #805EDE 0%, #453378 100%)"
                              : "#EFEBFB",
                          transition: "0.3s",
                          cursor: "pointer",
                        }}
                        className="client__stats__bar"
                        onMouseEnter={() => handleBarHover(item, index)}
                        onClick={() =>
                          handleBarClickWithDate(
                            item.filterKey,
                            item.weekStarts[index]
                          )
                        }
                      />
                    </Box>
                
                    <Typography className="client__stats__bar-label">
                      <span className="client__stats__bar-label-w">W</span>
                      <span className="client__stats__bar-label-num">
                        -{item.bars.length - index}
                      </span>
                    </Typography>
                
                  </Box>
                </Tooltip>                
                )})}
              </Box>
            </Box>
          </div>
        )})}
      </div>
      <SlideOver isOpen={isSlideOverOpen} onClose={() => {setIsSlideOverOpen(false), setFilterResetSignal(prev => prev + 1), setSelectedFilter(""), localStorage.removeItem(INTERACTION_FILTER_KEY); localStorage.removeItem(INTERACTION_DATE_RANGE_KEY);}}>
        <ClientInteractionSlideOverview 
          isSlideOverOpen={isSlideOverOpen} 
          filterKey={selectedFilter} 
          filterReset={filterResetSignal} 
          slideOverParent={slideOverParent}
          parentPageIdentifier={parentPageIdentifier} 
          customDateRange={customDateRange}
          />
      </SlideOver>
    </div>
  );
};

export default ClientInteractionQuality;
